import React from 'react';
import { Link } from 'react-router-dom';
import './EmptyState.css';

/**
 * The one way this app renders "there is nothing here yet".
 *
 * Every screen already had an empty case, but they were bare sentences —
 * "No trips yet.", "No followers yet." — which on a brand-new account is most of
 * the product. A first-run tester could not tell a working-but-empty screen from
 * a broken one, and nothing told them what to do about it. Hence the shape:
 * a title that names the state, a line that explains WHY it is empty, and,
 * wherever one exists, the single action that fills it.
 *
 * `action` is optional on purpose. Some empty states genuinely have no next step
 * the user can take here (an empty notifications inbox fills up because other
 * people act), and inventing a button for those is worse than the sentence.
 */

interface EmptyStateAction {
  label: string;
  /** Internal route. Use this OR onClick, not both. */
  to?: string;
  onClick?: () => void;
}

interface EmptyStateProps {
  title: string;
  body?: string;
  action?: EmptyStateAction;
  /** Inline inside a populated page (a profile tab, a sidebar) rather than owning the viewport. */
  compact?: boolean;
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ title, body, action, compact, className }) => (
  <div
    className={`v2-empty-state ${compact ? 'v2-empty-state--compact' : ''} ${className || ''}`.trim()}
  >
    <h3 className="v2-empty-state__title">{title}</h3>
    {body && <p className="v2-empty-state__body">{body}</p>}
    {action && (action.to ? (
      <Link className="v2-empty-state__action" to={action.to}>
        {action.label}
      </Link>
    ) : (
      <button type="button" className="v2-empty-state__action" onClick={action.onClick}>
        {action.label}
      </button>
    ))}
  </div>
);

export default EmptyState;
